import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { KeyRound, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(t("reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password, name: name || undefined });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm bg-white border border-border rounded-md p-8 animate-fade-in-up">
        <div className="flex flex-col items-center mb-7">
          <div className="w-11 h-11 rounded-md border border-border flex items-center justify-center mb-3">
            <KeyRound size={18} className="text-brand" />
          </div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-foreground">{t("reset.title")}</h1>
        </div>

        {done ? (
          <div className="flex items-center gap-2 text-sm text-green-700 border border-green-200 bg-green-50 rounded-md px-3 py-3" data-testid="reset-success-message">
            <CheckCircle2 size={15} /> {t("reset.success")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label htmlFor="new-name">{t("reset.newName")}</Label>
              <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="reset-name-input" placeholder={t("reset.namePlaceholder")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{t("reset.newPassword")}</Label>
              <Input id="new-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">{t("reset.confirmPassword")}</Label>
              <Input id="confirm-password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="reset-error-message">
                <AlertTriangle size={14} /> {error}
              </div>
            )}
            <Button type="submit" disabled={loading || !token} data-testid="reset-submit-button" className="w-full bg-brand hover:bg-brand-hover rounded-md h-10 font-semibold transition-colors">
              {loading ? "..." : t("reset.submit")}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              <Link to="/login" className="text-brand hover:underline" data-testid="back-to-login-link">{t("reset.backToLogin")}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
